import AutoCheckbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/AutoCheckbox';
import Checkbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/Checkbox';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { MagnifyingGlass } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { escapeRegExp } from 'es-toolkit';
import * as React from 'react';
import isEqual from 'react-fast-compare';
import { connect } from 'react-redux';
import styled, { css } from 'styled-components';

import { ScrollBar } from '@/components/ScrollBar/ScrollBar';
import SeeMoreItems from '@/components/SeeMoreItems/SeeMoreItems';
import noop from '@/utils/noop';
import { modifyParamsRemovePage } from '@/utils/queryParams';
import { getCategoryFacetLink } from '@/utils/urls';

import ExcludeFacetMenu from './ExcludeFacetMenu';
import Facet from './Facet';
import { StyledContainer } from './helpers/excludePopupStyles';
import MobileExcludeConfirm from './MobileExcludeConfirm';

const MAX_HEIGHT_FOR_CHECKS = 400;
const WEB_FACET_LIMIT = 6;
const MOBILE_FACET_LIMIT = 4;
const MAX_HEIGHT_FOR_SEE_MORE = 400;

export interface MultiCheckFacetProps {
    checklist: any[];
    emptyMessage: React.ReactNode;
    excludable?: boolean;
    facetId: string;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    forceEnabled: boolean;
    isMobileView?: boolean;
    loadPageWithFacetCollapsed?: boolean;
    queryParams: any;
    searchPlaceholder?: string;
    showAll: boolean;
    showCounts: boolean;
    showLandingPageLinks?: boolean;
    showSearchHeader: boolean;
    title: string;
    userIsLoggedIn?: boolean;
}

type State = {
    excludeHouseId: number | string;
    filteredList: any[];
    searchText: string;
    showScrollBar: boolean;
};

class MultiCheckFacet extends React.Component<MultiCheckFacetProps, State> {
    static defaultProps = {
        excludable: false,
        forceEnabled: false,
        isMobileView: false,
        loadPageWithFacetCollapsed: false,
        showAll: false,
        showCounts: true,
        showLandingPageLinks: false,
    };

    state = {
        excludeHouseId: 0,
        filteredList: this.props.checklist,
        searchText: '',
        showScrollBar: false,
    };

    shouldComponentUpdate = (nextProps: MultiCheckFacetProps, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: MultiCheckFacetProps) {
        if (prevProps.checklist.length !== this.props.checklist.length) {
            this.setState({
                filteredList: this.props.checklist,
                searchText: '',
                showScrollBar: false,
            });
        } else if (this.state.searchText !== '') {
            const filteredList = this.filterList(this.state.searchText);
            this.setState({ filteredList });
        } else {
            this.setState({ filteredList: this.props.checklist });
        }
    }

    /**
     * showExcludeByHouseId reveals Auction House facet search exclusion CTA
     * @param houseId if > 0, reveals search houseExclusion; 0 hides CTA
     */
    showExcludeByHouseId = (houseId: number | string) => {
        this.setState({ excludeHouseId: houseId });
    };

    onConfirmExclusion = () => {
        // hides house exclusion foldout CTA
        this.showExcludeByHouseId(0);
    };

    onSeeMoreItemsClick = () => {
        const showScrollBar = !this.state.showScrollBar;
        this.setState({ showScrollBar });
    };

    filterList = (searchText: string) => {
        const search = new RegExp(`(?:^|\\W)${escapeRegExp(searchText)}`, 'i');
        return this.props.checklist.filter((checkListItem) => search.test(checkListItem.name));
    };

    filterListEvent = (event: any) => {
        const filteredList = this.filterList(event.target.value);
        this.setState({ filteredList, searchText: event.target.value });
    };

    check = (id: string, checked: boolean) => {
        this.props.facetOptionChecked(this.props.facetId, id, checked);
    };

    onCheckHandler = (id: string, event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        const newCheckStatus = event.currentTarget.checked;
        event.stopPropagation();
        this.props.facetOptionChecked(this.props.facetId, id, newCheckStatus);
    };

    mapHyperLinkFilteredList = ({ count, excluded, id, landingPageUrl, name, selected }: any) => {
        let { queryParams } = this.props;
        queryParams = modifyParamsRemovePage(queryParams);
        const disabled = count === 0 || excluded;
        const showCounts = this.props.showCounts && !excluded;

        const resultsCountNode = showCounts && (
            <Caption
                className="ml-[4px]"
                suppressHydrationWarning
            >
                ({Number(count).toLocaleString()})
            </Caption>
        );

        const excludeFacetMenu = this.props.excludable && (
            <ExcludeFacetMenu
                excludeByHouseId={this.showExcludeByHouseId}
                isMobileView={this.props.isMobileView}
                optionId={id}
            />
        );

        return (
            <StyledExcludedContainer
                className={cn('group', excluded)}
                key={`${name}-${id}-options`}
            >
                <StyledFacetOptionLink
                    route
                    to={getCategoryFacetLink(landingPageUrl, queryParams)}
                >
                    <StyledCheckbox
                        checked={selected}
                        disabled={disabled}
                        label={name}
                        labelDisplay=""
                        name={id}
                        onClick={noop}
                    />
                    <StyledNameContainer>
                        <BodySecondary>{name}</BodySecondary>
                        {resultsCountNode}
                    </StyledNameContainer>
                </StyledFacetOptionLink>
                {excludeFacetMenu}
            </StyledExcludedContainer>
        );
    };

    mapFilteredList = ({ count, excluded, id, name: initialName, selected, value }: any) => {
        let { forceEnabled, showCounts } = this.props;
        const disabled = (count === 0 || excluded) && !forceEnabled;
        // because its possible name could be "null" we'll add an extra check here to ensure that its a string
        const name = initialName === 'Pay Online' ? 'Pay On LiveAuctioneers' : initialName || '';

        const resultsCountNode = showCounts && (
            <Caption
                className="ml-[4px]"
                suppressHydrationWarning
            >
                ({Number(count).toLocaleString()})
            </Caption>
        );

        // expensive: only render and show if excludable is set

        let excludeFacetMenu: React.ReactElement;
        if (this.props.excludable && this.props.userIsLoggedIn) {
            const disabled = Boolean(this.state.excludeHouseId);
            excludeFacetMenu = this.props.isMobileView ? (
                <StyledMobileExcludeLink
                    data-testid="mobile-link"
                    disabled={disabled}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                        event.stopPropagation();
                        this.showExcludeByHouseId(id);
                    }}
                >
                    <FormattedMessage id="search_facet_add_exclusion" />
                </StyledMobileExcludeLink>
            ) : (
                <ExcludeFacetMenu
                    data-testid="desktop-link"
                    excludeByHouseId={this.showExcludeByHouseId}
                    isMobileView={this.props.isMobileView}
                    optionId={id}
                />
            );
        }

        // TODO: remove the `id || value` constructs, and fix the `auctionCalendar.js` module
        // to use the data structure patterns from `search.js` for consistency. (see user.js)
        const facetLineItem = (
            <StyledContainer key={`${name}-${id}-option`}>
                <StyledFacetContainer
                    data-testid="facet-container"
                    onClick={() => this.check(id, !selected)}
                >
                    <StyledAutoCheckbox
                        disabled={disabled}
                        key={`${selected}`}
                        label={name}
                        labelDisplay=""
                        name={id || value}
                        onChange={(event) => this.onCheckHandler(id, event)}
                        startChecked={selected}
                    />
                    <StyledNameContainer data-testid={`${name?.replace(/\s/g, '')}FacetItem`}>
                        <BodySecondary>{name}</BodySecondary>
                        {name === 'Top Rated' && (
                            <StyledTopImage
                                alt="badge"
                                src="https://p1.liveauctioneers.com/dist/images/badge.svg"
                            />
                        )}
                        {resultsCountNode}
                    </StyledNameContainer>
                    {excludeFacetMenu}
                </StyledFacetContainer>
            </StyledContainer>
        );

        if (this.props.isMobileView && this.state.excludeHouseId) {
            return this.state.excludeHouseId === id ? (
                <React.Fragment key={id}>
                    {facetLineItem}
                    <MobileExcludeConfirm
                        onConfirmExclusion={this.onConfirmExclusion}
                        optionId={id}
                        optionName={name}
                    />
                </React.Fragment>
            ) : undefined;
        } else {
            return facetLineItem;
        }
    };

    render() {
        const {
            emptyMessage,
            loadPageWithFacetCollapsed,
            searchPlaceholder,
            showLandingPageLinks,
            showSearchHeader,
            title = '',
        } = this.props;

        const { filteredList, showScrollBar } = this.state;
        let list: React.ReactNode[] = [];
        if (filteredList.length === 0) {
            list.push(emptyMessage);
        } else {
            for (let i = 0; i < filteredList.length; i += 1) {
                list.push(
                    filteredList[i].landingPageUrl && showLandingPageLinks
                        ? this.mapHyperLinkFilteredList(filteredList[i])
                        : this.mapFilteredList(filteredList[i])
                );
            }
        }
        list = list.filter((lineItem) => lineItem !== undefined);
        const placeholderText = searchPlaceholder || `Search ${title}s`;
        const search = showSearchHeader && (
            <StyledSearchInput
                aria-label={placeholderText}
                hideSpacer
                iconRight={
                    <MagnifyingGlass
                        className="cursor-pointer text-icon-primary hover:text-link-hover"
                        size="sm"
                    />
                }
                id={placeholderText}
                onChange={this.filterListEvent}
                placeholder={placeholderText}
                value={this.state.searchText}
            />
        );

        const maxHeight = showScrollBar ? MAX_HEIGHT_FOR_SEE_MORE : MAX_HEIGHT_FOR_CHECKS;
        const facetClass = title.trim().replace('/', '').replace(/\s+/g, '-').toLowerCase();
        const shortListSliceCount = this.props.isMobileView ? MOBILE_FACET_LIMIT : WEB_FACET_LIMIT;

        return (
            <Facet
                className={`multicheck-facet ${facetClass}`}
                closeFacet={loadPageWithFacetCollapsed}
                header={search}
                testid={facetClass}
                title={title}
            >
                <StyledScrollBar
                    maxHeight={maxHeight}
                    showScrollBar={showScrollBar}
                >
                    <SeeMoreItems
                        hideSeeLess
                        itemsComponentFull={list}
                        itemsComponentShort={list.slice(0, shortListSliceCount)}
                        itemsListLengthFull={list.length}
                        itemsListLengthShort={list.slice(0, shortListSliceCount).length}
                        onSeeMoreItemsClick={this.onSeeMoreItemsClick}
                        showAllItems={this.props.showAll}
                    />
                </StyledScrollBar>
            </Facet>
        );
    }
}

export default connect(() => ({}))(MultiCheckFacet);

const StyledScrollBar = styled(ScrollBar)`
    flex-flow: row nowrap;
    color: ${({ theme }) => theme.colors.black};
    user-select: none;
    justify-content: flex-start;
    text-decoration: none;
    position: relative;
`;

const StyledNameContainer = styled.span`
    align-items: center;
    position: relative;
    flex: 0 1 auto;
    display: flex;
    flex-grow: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
    margin-left: 5px;
    margin-right: 10px;
`;

const StyledExcludedContainer = styled.div`
    input[type='checkbox']:checked + ${/* sc-sel */ StyledNameContainer}::before {
        background-color: ${({ theme }) => theme.colors.grey400};
    }

    &:hover ${/* sc-sel */ StyledNameContainer} > span {
        color: inherit;
        text-decoration: none;
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    }
`;

const StyledFacetContainer = styled.div<{ excluded?: boolean }>`
    display: flex;
    align-items: flex-start;
    flex-flow: row nowrap;
    color: ${({ theme }) => theme.colors.black};
    user-select: none;
    justify-content: flex-start;
    text-decoration: none;
    position: relative;
    padding-bottom: 4px;
    padding-top: 4px;
    padding-left: 8px;
    margin-left: -2px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-left: 0;
        padding-bottom: 16px;
        padding-top: 16px;
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.grey400};
        color: ${({ theme }) => theme.colors.black};
        text-decoration: none;
        cursor: pointer;
    }

    ${({ excluded }) =>
        excluded
            ? css`
                  input[type='checkbox']:checked + ${StyledNameContainer}::before {
                      background-color: ${({ theme }) => theme.colors.grey400};
                  }

                  &:hover ${StyledNameContainer} > span {
                      color: inherit;
                      text-decoration: none;
                  }

                  &:hover {
                      display: flex;
                      background-color: ${({ theme }) => theme.colors.shadowOverlay50};
                  }
              `
            : ''};
`;

const StyledSearchInput = styled(Input)`
    margin-bottom: 16px;
    padding-left: 8px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-bottom: 32px;
    }
`;

const StyledTopImage = styled.img`
    width: 18px;
    height: 17px;
    vertical-align: middle;
    margin: 2px 2px 0 7px;
`;

const StyledMobileExcludeLink = styled(Link)<{ disabled?: boolean }>`
    ${({ disabled }) =>
        disabled
            ? css`
                  color: ${({ theme }) => theme.colors.grey400};
              `
            : ''}
`;

const StyledCheckbox = styled(Checkbox)`
    display: flex;
    flex: 0 0 25px;
    flex-flow: row nowrap;
    min-width: 0;
    padding: 0;
    margin: 2px 0 0 1px;
    height: 18px;
    font-family: ${({ theme }) => theme.typography.fontPrimary};

    ${/* sc-sel */ StyledNameContainer}::before {
        display: inline-block;
        height: 1em;
        width: 1em;
        border: 1px solid ${({ theme }) => theme.colors.grey100};
        margin: 0 6px 0 0;
        vertical-align: -0.125em;
    }

    &:checked + ${/* sc-sel */ StyledNameContainer}::before {
        background-color: ${({ theme }) => theme.colors.blue200};
    }

    &:checked + ${/* sc-sel */ StyledNameContainer}::after {
        display: block;
        position: absolute;
        left: 0.375em;
        top: 0.25em;
        width: 5px;
        height: 10px;
        border: solid ${({ theme }) => theme.colors.white};
        border-width: 0 2px 2px 0;
        transform: rotate(40deg);
    }
`;
const StyledAutoCheckbox = styled(AutoCheckbox)`
    display: flex;
    flex: 0 0 25px;
    flex-flow: row nowrap;
    min-width: 0;
    padding: 0;
    margin: 2px 0 0 1px;
    height: 18px;
    font-family: ${({ theme }) => theme.typography.fontPrimary};

    ${/* sc-sel */ StyledNameContainer}::before {
        display: inline-block;
        height: 1em;
        width: 1em;
        border: 1px solid ${({ theme }) => theme.colors.grey100};
        margin: 0 6px 0 0;
        vertical-align: -0.125em;
    }

    &:checked + ${/* sc-sel */ StyledNameContainer}::before {
        background-color: ${({ theme }) => theme.colors.blue200};
    }

    &:checked + ${/* sc-sel */ StyledNameContainer}::after {
        display: block;
        position: absolute;
        left: 0.375em;
        top: 0.25em;
        width: 5px;
        height: 10px;
        border: solid ${({ theme }) => theme.colors.white};
        border-width: 0 2px 2px 0;
        transform: rotate(40deg);
    }
`;

const StyledFacetOptionLink = styled(Link)`
    display: flex;
    flex-flow: row nowrap;
    color: ${({ theme }) => theme.colors.black};
    user-select: none;
    justify-content: flex-start;
    text-decoration: none;
    position: relative;
    padding-bottom: 4px;
    padding-top: 4px;
    padding-left: 8px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding-bottom: 16px;
        padding-top: 16px;
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.grey400};
        color: ${({ theme }) => theme.colors.black};
        text-decoration: none;
        cursor: pointer;
    }
`;
